import React, { useState, useEffect, useCallback } from "react";
import MessageIdentitySettings from "./MessageIdentitySettings";
import ConsentFormSettings from "./ConsentFormSettings";
import { fetchTenantConfigs, updateTenantConfigs } from "../../api/tenantConfigApi";
import {
  fetchTenantProfile,
  updateTenantProfile,
  activateTenantProfile,
  deactivateTenantProfile,
} from "../../api/tenantProfileApi";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_LABELS = { monday: "Mon", tuesday: "Tue", wednesday: "Wed", thursday: "Thu", friday: "Fri", saturday: "Sat", sunday: "Sun" };

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs font-medium text-slate-600 capitalize">{label.replace(/_/g, " ")}</label>
    {children}
  </div>
);

const TextInput = ({ value, onChange, disabled, placeholder }) => (
  <input
    type="text"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    placeholder={placeholder}
    className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/20 disabled:cursor-not-allowed disabled:opacity-50"
  />
);

const Textarea = ({ value, onChange, disabled, placeholder }) => (
  <textarea
    value={value}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    placeholder={placeholder}
    rows={3}
    className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/20 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
  />
);

const NumberInput = ({ value, onChange, disabled, min, max }) => (
  <input
    type="number"
    value={value}
    onChange={(e) => onChange(Number(e.target.value))}
    disabled={disabled}
    min={min}
    max={max}
    className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/20 disabled:cursor-not-allowed disabled:opacity-50"
  />
);

const SelectInput = ({ value, onChange, disabled, options }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/20 disabled:cursor-not-allowed disabled:opacity-50"
  >
    {options.map((o) => (
      <option key={o} value={o}>{o}</option>
    ))}
  </select>
);

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#096dea]/30 disabled:cursor-not-allowed disabled:opacity-50 ${
      checked ? "bg-[#096dea]" : "bg-slate-200"
    }`}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
        checked ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

// const ColorInput = ({ value, onChange, disabled }) => (
//   <div className="flex items-center gap-2">
//     <input
//       type="color"
//       value={value || "#000000"}
//       onChange={(e) => onChange(e.target.value)}
//       disabled={disabled}
//       className="h-9 w-9 cursor-pointer rounded-lg border border-slate-200 p-0.5 disabled:cursor-not-allowed disabled:opacity-50"
//     />
//     <input
//       type="text"
//       value={value}
//       onChange={(e) => onChange(e.target.value)}
//       disabled={disabled}
//       className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/20 disabled:cursor-not-allowed disabled:opacity-50"
//     />
//   </div>
// );

const SectionHeader = ({ title, description }) => (
  <div className="mb-4">
    <h4 className="text-sm font-semibold text-slate-950">{title}</h4>
    <p className="mt-1 text-xs text-slate-500">{description}</p>
  </div>
);

const SaveButton = ({ saving, onClick, disabled }) => (
  <div className="pt-2 flex items-center gap-3">
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || saving}
      className="rounded-xl bg-[#096dea] px-4 py-2 text-xs font-semibold text-white transition hover:bg-[#0959bb] disabled:cursor-not-allowed disabled:opacity-50"
    >
      {saving ? "Saving..." : "Save changes"}
    </button>
  </div>
);

const getConfig = (configs, key) => configs?.find((c) => c.config_key === key);

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });



// --- Business Profile ---
const BusinessProfileSection = ({ tenantId, configs, disabled, onSave }) => {
  const existing = getConfig(configs, "business_profile");
  const [fields, setFields] = useState(() => ({
    heading: "",
    description: "",
    address: "",
    website: "",
    support_email: "",
    support_phone: "",
    logo_data: "",
    logo_mime_type: "",
    ...(existing?.config_json || {}),
  }));
  const [isActivated, setIsActivated] = useState(false);
  const [activatedAt, setActivatedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);
  const [logoError, setLogoError] = useState("");
  const [profileError, setProfileError] = useState("");
  const [profileLoading, setProfileLoading] = useState(Boolean(tenantId));
  const [profileLoadFailed, setProfileLoadFailed] = useState(false);
  const [slug, setSlug] = useState("");

  useEffect(() => {
    if (!tenantId) return;
    let isMounted = true;
    setProfileLoading(true);
    setProfileError("");
    setProfileLoadFailed(false);
    fetchTenantProfile(tenantId)
      .then((res) => {
        if (!isMounted || !res?.data) return;
        const data = res.data;
        setSlug(data.slug || "");
        setFields((prev) => ({
          ...prev,
          heading: data.heading || prev.heading,
          description: data.description || prev.description,
          logo_data: data.logo_data || prev.logo_data,
          logo_mime_type: data.logo_mime_type || prev.logo_mime_type,
        }));
        setIsActivated(data.is_profile_public ?? Boolean(data.activated_at && !data.deactivated_at));
        setActivatedAt(data.activated_at || null);
      })
      .catch((err) => {
        if (isMounted) {
          const status = err.status || err.response?.status;
          const detail = err.details?.detail || err.response?.data?.detail;
          if (status === 404 && detail === `Profile for bot ${tenantId} not found`) {
            setIsActivated(false);
            setActivatedAt(null);
            return;
          }
          setProfileLoadFailed(true);
          setProfileError(err.message || "Unable to load business profile.");
        }
      })
      .finally(() => {
        if (isMounted) setProfileLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [tenantId]);

  const handleLogoChange = async (file) => {
    setLogoError("");
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setLogoError("Logo must be 5MB or smaller.");
      return;
    }
    try {
      const logoData = await readFileAsBase64(file);
      setFields((p) => ({
        ...p,
        logo_data: logoData,
        logo_mime_type: file.type,
      }));
    } catch (_) {
      setLogoError("Unable to read selected logo.");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setProfileError("");
    try {
      if (tenantId) {
        const response = await updateTenantProfile(tenantId, {
          heading: fields.heading,
          description: fields.description,
          logo_data: fields.logo_data,
          logo_mime_type: fields.logo_mime_type,
        });
        if (response?.data) {
          setIsActivated(response.data.is_profile_public ?? Boolean(response.data.activated_at && !response.data.deactivated_at));
          setActivatedAt(response.data.activated_at || null);
        }
      }
      if (onSave) {
        const { heading, logo_data, logo_mime_type, ...allowedConfigFields } = fields;
        await onSave("business_profile", allowedConfigFields);
      }
    } catch (err) {
      setProfileError(err.message || "Unable to save business profile.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActivation = async () => {
    if (!tenantId) return;
    setTogglingActive(true);
    setProfileError("");
    try {
      if (isActivated) {
        const res = await deactivateTenantProfile(tenantId);
        setIsActivated(false);
        setActivatedAt(res?.data?.activated_at || null);
      } else {
        // Ensure profile row exists before activation
        await updateTenantProfile(tenantId, {
          heading: fields.heading,
          description: fields.description,
          logo_data: fields.logo_data,
          logo_mime_type: fields.logo_mime_type,
        });

        const res = await activateTenantProfile(tenantId);
        setIsActivated(true);
        setActivatedAt(res?.data?.activated_at || new Date().toISOString());
      }
    } catch (err) {
      setProfileError(err.message || "Unable to change public profile visibility.");
    } finally {
      setTogglingActive(false);
    }
  };

  const logoPreview = fields.logo_data && fields.logo_mime_type
    ? `data:${fields.logo_mime_type};base64,${fields.logo_data}`
    : "";
  disabled = disabled || profileLoading || profileLoadFailed || saving || togglingActive;

  return (
    <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
        <div>
          <h4 className="text-sm font-semibold text-slate-950">General Information</h4>
          <p className="mt-1 text-xs text-slate-500">General business profile, public visibility, and branding.</p>
        </div>
        {tenantId ? (
          <div className="flex flex-wrap items-center gap-3">
            <a
              href={slug ? `/public/${encodeURIComponent(slug)}` : `/public/tenants/${tenantId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
            >
              <svg className="h-3.5 w-3.5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
              View Public Profile
            </a>
            {slug && <a href={`/public/${encodeURIComponent(slug)}/registration`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
              Open Registration Form
            </a>}
            <div className="flex items-center gap-3 rounded-2xl bg-slate-50 border border-slate-200 px-3 py-1.5">
              <span className="text-xs font-medium text-slate-600">Public Profile:</span>
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                  isActivated ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"
                }`}
              >
                {isActivated ? "Publicly Active" : "Hidden / Inactive"}
              </span>
              <Toggle
                checked={isActivated}
                onChange={handleToggleActivation}
                disabled={disabled || togglingActive}
              />
            </div>
          </div>
        ) : null}
      </div>

      {profileLoading && <p role="status" className="mb-3 text-sm text-slate-500">Loading business profile...</p>}
      {profileError && <p role="alert" className="mb-3 text-sm text-rose-700">{profileError}</p>}
      {tenantId && <p className="mb-3 text-xs text-slate-500">Enable the public profile and keep the agent active to allow customer registration. Share the registration form link or link to it from your website. OTP uses the WhatsApp authentication template configured under Forms.</p>}
      <div className="space-y-3">
        <Field label="Profile Heading">
          <TextInput value={fields.heading} onChange={(v) => setFields((p) => ({ ...p, heading: v }))} disabled={disabled} placeholder="Customer support for Example Co" />
        </Field>
        <Field label="Business Description">
          <Textarea value={fields.description} onChange={(v) => setFields((p) => ({ ...p, description: v }))} disabled={disabled} placeholder="Describe the business..." />
        </Field>
        <Field label="Logo">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            {logoPreview ? (
              <img src={logoPreview} alt="Business logo preview" className="h-14 w-14 rounded-xl border border-slate-200 object-contain" />
            ) : null}
            <input
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
              onChange={(e) => handleLogoChange(e.target.files?.[0])}
              disabled={disabled}
              className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-xl file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-slate-700 hover:file:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          {logoError ? <p className="text-xs text-rose-600">{logoError}</p> : null}
        </Field>
        <Field label="Address">
          <TextInput value={fields.address} onChange={(v) => setFields((p) => ({ ...p, address: v }))} disabled={disabled} placeholder="123 Main St, City, Country" />
        </Field>
        <Field label="Website">
          <TextInput value={fields.website} onChange={(v) => setFields((p) => ({ ...p, website: v }))} disabled={disabled} placeholder="https://example.com" />
        </Field>
        <Field label="Support Email">
          <TextInput value={fields.support_email} onChange={(v) => setFields((p) => ({ ...p, support_email: v }))} disabled={disabled} placeholder="support@example.com" />
        </Field>
        <Field label="Support Phone">
          <TextInput value={fields.support_phone} onChange={(v) => setFields((p) => ({ ...p, support_phone: v }))} disabled={disabled} placeholder="+1 (555) 000-0000" />
        </Field>
      </div>
      <SaveButton saving={saving} onClick={handleSave} disabled={disabled} />
    </article>
  );
};

// --- Business Hours ---
const BusinessHoursSection = ({ configs, disabled, onSave }) => {
  const existing = getConfig(configs, "business_hours");
  const [fields, setFields] = useState(() => {
    const defaults = { timezone: "" };
    DAYS.forEach((d) => { defaults[d] = { enabled: false, open: "", close: "" }; });
    return { ...defaults, ...(existing?.config_json || {}) };
  });
  const [saving, setSaving] = useState(false);

  const updateDay = (day, key, value) => {
    setFields((p) => ({ ...p, [day]: { ...p[day], [key]: value } }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave("business_hours", fields);
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <SectionHeader title="Operating Hours" description="Set operating hours for each day of the week." />
      <div className="space-y-2">
        <div className="grid grid-cols-[100px_50px_1fr_1fr] gap-2 px-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
          <span>Day</span>
          <span>On</span>
          <span>Open</span>
          <span>Close</span>
        </div>
        {DAYS.map((day) => (
          <div key={day} className="grid grid-cols-[100px_50px_1fr_1fr] gap-2 items-center">
            <span className="text-sm font-medium text-slate-700">{DAY_LABELS[day]}</span>
            <Toggle checked={fields[day]?.enabled || false} onChange={(v) => updateDay(day, "enabled", v)} disabled={disabled} />
            <input
              type="time"
              value={fields[day]?.open || ""}
              onChange={(e) => updateDay(day, "open", e.target.value)}
              disabled={disabled || !fields[day]?.enabled}
              className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/20 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <input
              type="time"
              value={fields[day]?.close || ""}
              onChange={(e) => updateDay(day, "close", e.target.value)}
              disabled={disabled || !fields[day]?.enabled}
              className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-[#096dea] focus:ring-2 focus:ring-[#096dea]/20 disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
        ))}
        <div className="pt-2">
          <Field label="Timezone">
            <TextInput value={fields.timezone} onChange={(v) => setFields((p) => ({ ...p, timezone: v }))} disabled={disabled} placeholder="America/New_York" />
          </Field>
        </div>
      </div>
      <SaveButton saving={saving} onClick={handleSave} disabled={disabled} />
    </article>
  );
};

// --- AI Settings ---
const AiSettingsSection = ({ configs, disabled, onSave }) => {
  const existing = getConfig(configs, "ai_settings");
  const [fields, setFields] = useState(() => ({
    tone: "neutral",
    language: "en",
    fallback_message: "",
    max_response_length: 500,
    enable_citations: false,
    ...(existing?.config_json || {}),
  }));
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave("ai_settings", fields);
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <SectionHeader title="AI Tone & Behavior" description="Configure AI behaviour, tone, and response parameters." />
      <div className="space-y-3">
        <Field label="Response Tone">
          <SelectInput value={fields.tone} onChange={(v) => setFields((p) => ({ ...p, tone: v }))} disabled={disabled} options={["Friendly", "Professional", "Direct"]} />
        </Field>
        <Field label="Language">
          <TextInput value={fields.language} onChange={(v) => setFields((p) => ({ ...p, language: v }))} disabled={disabled} placeholder="en" />
        </Field>
        <Field label="Fallback Message">
          <TextInput value={fields.fallback_message} onChange={(v) => setFields((p) => ({ ...p, fallback_message: v }))} disabled={disabled} placeholder="I could not find an answer to that." />
        </Field>
        <Field label="Max Response Length">
          <NumberInput value={fields.max_response_length} onChange={(v) => setFields((p) => ({ ...p, max_response_length: v }))} disabled={disabled} min={50} max={4000} />
        </Field>
        <div className="flex items-center gap-3">
          <Toggle checked={!!fields.enable_citations} onChange={(v) => setFields((p) => ({ ...p, enable_citations: v }))} disabled={disabled} />
          <span className="text-sm text-slate-700">Enable citations in responses</span>
        </div>
      </div>
      <SaveButton saving={saving} onClick={handleSave} disabled={disabled} />
    </article>
  );
};

// --- Notification Settings (commented out) ---
// --- Branding (commented out) ---

// --- Main Component ---
export const BasicSettings = ({ botId, disabled = false }) => {
  const [configs, setConfigs] = useState(null);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let active = true;
    setConfigs(null);
    setError("");
    setSaved(false);
    fetchTenantConfigs(botId).then((response) => {
      if (!active) return;
      const items = response?.data?.items || response?.data || response;
      if (!Array.isArray(items)) throw new Error("Invalid bot settings response.");
      setConfigs(items);
    }).catch((err) => {
      if (active) setError(err.message || "Unable to load basic settings.");
    });
    return () => { active = false; };
  }, [botId, retry]);

  const save = async (configKey, configJson) => {
    setSaved(false);
    await updateTenantConfigs(botId, { config_key: configKey, config_json: configJson });
    setSaved(true);
  };

  return <section aria-label="Basic settings" className="space-y-3">
    {error ? <p role="alert">{error}</p> : configs === null
      ? <p role="status">Loading basic settings...</p>
      : <BusinessProfileSection key={`${botId}-${retry}`} tenantId={botId} configs={configs} disabled={disabled} onSave={save} />}
    <button type="button" onClick={() => setRetry((value) => value + 1)} className="text-sm font-semibold text-cyan-800">Reload basic settings</button>
    {saved && <p role="status" className="text-sm text-emerald-700">Basic settings saved.</p>}
  </section>;
};

const TenantConfigTab = ({ tenantId, configs, disabled, onSave, hideOperatingHoursAndAiBehavior = false, hideBusinessProfile = false }) => {
  const handleSave = useCallback(
    async (configKey, configJson) => {
      if (onSave) await onSave(configKey, configJson);
    },
    [onSave]
  );

  return (
    <div className="space-y-6">
      {!hideBusinessProfile && <BusinessProfileSection tenantId={tenantId} configs={configs} disabled={disabled} onSave={handleSave} />}
      {tenantId && <MessageIdentitySettings key={`message-identity-${tenantId}`} botId={tenantId} disabled={disabled} />}
      {tenantId && <ConsentFormSettings key={`consent-forms-${tenantId}`} botId={tenantId} disabled={disabled} />}
      {!hideOperatingHoursAndAiBehavior ? (
        <>
          <BusinessHoursSection configs={configs} disabled={disabled} onSave={handleSave} />
          <AiSettingsSection configs={configs} disabled={disabled} onSave={handleSave} />
        </>
      ) : null}

    </div>
  );
};

export default TenantConfigTab;
